import { Skeleton } from './ui/skeleton';

/**
 * Reusable page-level skeleton loader.
 *
 * Variants:
 *  - dashboard: header strip + 4 stat cards + 2 wide rows
 *  - grid:      header + responsive card grid (default 6 tiles)
 *  - list:      header + stacked row skeletons
 *  - chat:      sidebar of conversations + main thread
 */
export default function PageSkeleton({ variant = 'grid', rows = 6 }) {
  if (variant === 'dashboard') {
    return (
      <div className="space-y-6" data-testid="page-skeleton-dashboard">
        <div className="space-y-3">
          <Skeleton className="h-4 w-40 bg-black/5" />
          <Skeleton className="h-10 w-72 bg-black/5" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 rounded-xl bg-black/5" />
          ))}
        </div>
        <Skeleton className="h-48 rounded-xl bg-black/5" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Skeleton className="h-40 rounded-xl bg-black/5" />
          <Skeleton className="h-40 rounded-xl bg-black/5" />
        </div>
      </div>
    );
  }

  if (variant === 'list') {
    return (
      <div className="space-y-4" data-testid="page-skeleton-list">
        <Skeleton className="h-8 w-48 bg-black/5" />
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-lg bg-black/5" />
        ))}
      </div>
    );
  }

  if (variant === 'chat') {
    return (
      <div className="flex gap-4 h-[600px]" data-testid="page-skeleton-chat">
        <div className="w-60 space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded-lg bg-black/5" />
          ))}
        </div>
        <div className="flex-1 space-y-3">
          <Skeleton className="h-20 w-3/4 rounded-2xl bg-black/5" />
          <Skeleton className="h-16 w-2/3 ml-auto rounded-2xl bg-black/5" />
          <Skeleton className="h-24 w-3/4 rounded-2xl bg-black/5" />
        </div>
      </div>
    );
  }

  // grid (default)
  return (
    <div className="space-y-4" data-testid="page-skeleton-grid">
      <Skeleton className="h-8 w-48 bg-black/5" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: rows }).map((_, i) => (
          <Skeleton key={i} className="h-36 rounded-xl bg-black/5" />
        ))}
      </div>
    </div>
  );
}
